import React, { useState } from 'react';
import { Linking, View } from 'react-native';
import { router } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, hoursText, money } from '@/api';
import { useAuth } from '@/auth';
import { fromServer, t } from '@/i18n';
import { actionsFor, invoiceLabel, STAGE_TONE } from '@/stages';
import { colors } from '@/theme';
import type { OrderSummary } from '@/types';
import { CallAgentPicker } from './CallAgentPicker';
import { Button, Card, ErrorNote, IconChip, Pill, s, T } from '@/ui';

/** `onChanged`: reload the list after a step done from the card (forwarding an incoming order). */
export function OrderCard({ order: o, quickActions, onChanged }: { order: OrderSummary; quickActions?: boolean; onChanged?: () => void }) {
  const { has } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false); // forwarding: choosing who calls
  const [agent, setAgent] = useState<number | undefined>(undefined);
  const tone = STAGE_TONE[o.stage] ?? STAGE_TONE.cancelled;
  const place = [o.address.zone, o.address.line].filter(Boolean).join(' · ');
  // Incoming orders: forward to the call center straight from the list.
  const canForward = o.stage === 'incoming' && actionsFor('incoming', has).includes('to_call');

  const forward = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/ops/orders/${o.id}/act`, { body: { action: 'to_call', call_user_id: agent ?? 0 } });
      onChanged?.();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Card onPress={() => router.push(`/order/${o.id}`)} accent={o.is_late ? colors.danger : o.stage === 'out_for_delivery' ? colors.turquoise : undefined}>
      <View style={s.between}>
        <T w="black" size={15}>{`#${o.reference ?? o.id}`}</T>
        <Pill label={fromServer(o.stage_label)} bg={tone.bg} fg={tone.fg} />
      </View>
      {o.customer_status === 'cancel_requested' ? (
        <View style={{ flexDirection: 'row', marginTop: 6 }}>
          <Pill label={t('الزبون طلب الإلغاء', 'Customer asked to cancel', 'کڕیار داوای هەڵوەشاندنەوەی کرد')} bg={colors.danger} fg={colors.white} />
        </View>
      ) : null}
      <T w="semibold" size={14} style={{ marginTop: 4 }}>{o.customer.name || '—'}</T>
      {place ? <T size={13} color={colors.muted} numberOfLines={2}>{place}</T> : null}

      <View style={[s.between, { marginTop: 8 }]}>
        <T w="black" size={16} color={colors.violet}>{money(o.money.to_collect)}</T>
        <View style={[s.row, { gap: 4 }]}>
          <Ionicons name={o.is_late ? 'alert-circle' : 'time-outline'} size={14} color={o.is_late ? colors.danger : colors.faint} />
          <T w={o.is_late ? 'bold' : 'regular'} size={12} color={o.is_late ? colors.danger : colors.muted}>
            {o.is_late
              ? t(`متأخر ${hoursText(o.hours_late)}`, `${hoursText(o.hours_late)} late`, `${hoursText(o.hours_late)} دواکەوتووە`)
              : t(`منذ ${hoursText(o.hours_in_stage)}`, `${hoursText(o.hours_in_stage)} ago`, `ماوەی ${hoursText(o.hours_in_stage)}`)}
          </T>
        </View>
      </View>

      {o.stage === 'new' ? (
        <View style={[s.row, { gap: 4, marginTop: 4 }]}>
          <Ionicons name={o.call_user ? 'person-outline' : 'people-outline'} size={13} color={colors.turquoiseDark} />
          <T size={12} w="semibold" color={colors.turquoiseDark}>
            {o.call_user ? `${t('موظف الاتصال', 'Call agent')}: ${o.call_user.name ?? ''}` : t('لكل موظفي الكول سنتر', 'All call-center agents')}
          </T>
        </View>
      ) : null}

      {o.driver || o.call_attempts > 0 ? (
        <T size={12} color={colors.muted} style={{ marginTop: 2 }}>
          {o.driver ? `${t('السائق', 'Driver')}: ${o.driver.name}` : `${t('محاولات الاتصال', 'Call attempts')}: ${o.call_attempts}`}
        </T>
      ) : null}
      {o.carrier ? (
        <T size={12} color={colors.turquoiseDark} style={{ marginTop: 2 }}>
          {`${t('شركة الشحن', 'Shipping company')}: ${o.carrier.name}${o.carrier.waybill ? ` · ${o.carrier.waybill}` : ''}${o.carrier.status ? ` · ${o.carrier.status}` : ''}`}
        </T>
      ) : null}

      {quickActions && (o.customer.phone || o.address.map_url || o.invoices?.length) ? (
        <View style={[s.row, { gap: 8, marginTop: 10, flexWrap: 'wrap' }]}>
          {o.customer.phone ? <IconChip icon="call" label={t('اتصال', 'Call')} onPress={() => Linking.openURL(`tel:${o.customer.phone}`)} /> : null}
          {o.address.map_url ? <IconChip icon="navigate" label={t('الخريطة', 'Map')} tone="turquoise" onPress={() => Linking.openURL(o.address.map_url!)} /> : null}
          {(o.invoices ?? []).map((inv) => (
            <IconChip key={inv.lang} icon="document-text-outline" label={invoiceLabel(inv.lang)} onPress={() => Linking.openURL(inv.url)} />
          ))}
        </View>
      ) : null}

      {canForward ? (
        <View style={{ marginTop: 10, gap: 8 }}>
          {picking ? (
            <>
              <CallAgentPicker orderId={o.id} value={agent} onChange={setAgent} />
              <View style={[s.row, { gap: 8 }]}>
                <Button label={t('تحويل', 'Forward')} icon="arrow-redo-outline" onPress={forward} loading={busy} disabled={agent === undefined} style={{ flex: 1 }} />
                <Button label={t('إلغاء', 'Cancel')} kind="outline" onPress={() => setPicking(false)} style={{ flex: 1 }} />
              </View>
            </>
          ) : (
            <Button label={t('تحويل للكول سنتر', 'Forward to call center')} icon="arrow-redo-outline" onPress={() => setPicking(true)} />
          )}
          <ErrorNote text={error} />
        </View>
      ) : null}
    </Card>
  );
}
